/**
 * Read/write helper for the IndexedDB database the Vue ERP uses for its
 * own session caches. Database = `LocalCache`, object stores = `workFlow`,
 * `metadata`, `permission`, `allUserDetails`. Entry shape is the simple
 * `{ id, value }` pair Vue writes via
 * `RW-ERP-Service/src/composables/mastery/mastery-store-db.ts` and
 * `bulkInsertIntoIndexedDB`.
 *
 * Sharing the database lets the React app read what the Vue app already
 * cached on login (instant render) and write back so Vue picks up React's
 * fetches too.
 *
 * **The React side never modifies the DB structure.** It opens at the
 * current version and reads/writes only the stores Vue already created.
 * If the requested store is missing (e.g. user has not yet visited the
 * Vue ERP this session), the helper resolves to `undefined` / no-op write
 * and the caller falls through to a network fetch. This avoids racing
 * Vue on `versionchange` upgrades and keeps the schema solely under Vue's
 * control.
 */
import { logger } from './logger';

const DB_NAME = 'LocalCache';

const formatDbError = (err: DOMException | null): string => {
  if (err === null) return 'unknown';
  return err.message || err.name || 'unknown';
};

export const VUE_CACHE_STORES = {
  metadata: 'metadata',
  workFlow: 'workFlow',
  /**
   * Cache slot for the REAL user's permission snapshot. Read/written
   * when `localStorage.isEmulate !== 'true'`.
   *
   * Vue parity: `LOCAL_CACHE_STORE_KEY.LOCAL_PERMISSION_DATA`
   * (user-permission.ts:82-86, 90-95).
   */
  permission: 'permission',
  /**
   * Cache slot for the EMULATED user's permission snapshot. Read/written
   * when `localStorage.isEmulate === 'true'`. Keeping it in a separate
   * IDB store means the real user's cache survives across emulation
   * sessions — toggling back doesn't trigger a re-fetch.
   *
   * Vue parity: `LOCAL_CACHE_STORE_KEY.LOCAL_EMULATED_PERMISSION_DATA`
   * (user-permission.ts:117-122).
   */
  emulatedPermission: 'emulatedPermission',
  allUserDetails: 'allUserDetails',
} as const;

export type VueCacheStoreName =
  (typeof VUE_CACHE_STORES)[keyof typeof VUE_CACHE_STORES];

/** Vue stores values keyed by `id`. The default key for the body payload is `'data'`. */
export const VUE_CACHE_KEY_DATA = 'data';

interface VueCacheEntry<T> {
  id: string;
  value: T;
}

const isIdbAvailable = (): boolean => {
  try {
    return typeof indexedDB !== 'undefined';
  } catch {
    return false;
  }
};

/**
 * Open `LocalCache` at its current version. Resolves to `null` if the
 * database does not exist yet OR if the requested store is missing.
 * Does NOT bump version or create stores — strict read-at-current.
 *
 * Used by the read path and by writes that should be skipped when the
 * schema isn't there (deliberate no-op for read-only contexts).
 */
function openLocalCache(store: VueCacheStoreName): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (!isIdbAvailable()) {
      resolve(null);
      return;
    }
    const request = indexedDB.open(DB_NAME);
    request.onerror = () => {
      logger.warn('[vueLocalCache] failed to open LocalCache', request.error);
      resolve(null);
    };
    request.onsuccess = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(store)) {
        db.close();
        resolve(null);
        return;
      }
      resolve(db);
    };
  });
}

/**
 * Open `LocalCache`, ensuring the requested store exists. If the store is
 * missing, the connection is closed and the DB reopened at the next
 * version, creating the store on the `onupgradeneeded` callback.
 *
 * Mirrors Vue's own dynamic-version pattern in
 * `RW-ERP-Service/src/composables/mastery/mastery-store-db.ts:25-69`. Used
 * by every write path so cache writes actually persist on the React
 * origin (where the schema may not have been created by anyone yet).
 */
function rawEnsureLocalCacheStore(
  store: VueCacheStoreName,
): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (!isIdbAvailable()) {
      resolve(null);
      return;
    }
    const initial = indexedDB.open(DB_NAME);
    initial.onerror = () => {
      logger.warn('[vueLocalCache] failed to open LocalCache', initial.error);
      resolve(null);
    };
    initial.onsuccess = () => {
      const db = initial.result;
      if (db.objectStoreNames.contains(store)) {
        resolve(db);
        return;
      }
      const nextVersion = db.version + 1;
      db.close();
      const upgrade = indexedDB.open(DB_NAME, nextVersion);
      upgrade.onupgradeneeded = () => {
        const upgradedDb = upgrade.result;
        if (!upgradedDb.objectStoreNames.contains(store)) {
          upgradedDb.createObjectStore(store, { keyPath: 'id' });
        }
      };
      upgrade.onsuccess = () => {
        logger.info('[vueLocalCache] created store', {
          store,
          version: nextVersion,
        });
        resolve(upgrade.result);
      };
      upgrade.onerror = () => {
        logger.warn(
          '[vueLocalCache] failed to upgrade LocalCache',
          { store, error: formatDbError(upgrade.error) },
        );
        resolve(null);
      };
      upgrade.onblocked = () => {
        logger.warn(
          '[vueLocalCache] upgrade blocked — another tab/connection holds an older version',
          { store },
        );
        resolve(null);
      };
    };
  });
}

/**
 * Serialised wrapper for `rawEnsureLocalCacheStore`.
 *
 * IndexedDB has a global lock on schema changes: while one open request
 * with a new version is upgrading, any *other* open request that requires
 * an even newer version is blocked. When multiple stores need to be
 * created on first run (permission + metadata + workFlow + …), running
 * the upgrades in parallel ends in:
 *
 *   1. Caller A opens at v1, sees store-A missing, reopens at v2.
 *   2. Caller B opens at v1 in parallel, sees store-B missing, reopens at v2.
 *   3. B's open is now BLOCKED because A holds an upgrade-in-progress
 *      at v2. B's `onblocked` fires → we resolve null → the write is a
 *      silent no-op → cache stays empty.
 *
 * Serialising every `ensureLocalCacheStore` call through one promise
 * chain eliminates the race: A finishes its upgrade, releases the lock,
 * then B opens, sees v2, sees store-B still missing (A only created A),
 * and upgrades to v3 cleanly.
 */
let ensureChain: Promise<unknown> = Promise.resolve();

function ensureLocalCacheStore(
  store: VueCacheStoreName,
): Promise<IDBDatabase | null> {
  const next = ensureChain
    .catch(() => undefined)
    .then(() => rawEnsureLocalCacheStore(store));
  ensureChain = next;
  return next;
}

export async function readVueCache<T>(
  store: VueCacheStoreName,
  id: string = VUE_CACHE_KEY_DATA,
): Promise<T | undefined> {
  const db = await openLocalCache(store);
  if (!db) return undefined;
  return new Promise<T | undefined>((resolve) => {
    try {
      const tx = db.transaction(store, 'readonly');
      const req = tx.objectStore(store).get(id);
      req.onsuccess = () => {
        const entry = req.result as VueCacheEntry<T> | undefined;
        resolve(entry?.value);
      };
      req.onerror = () => {
        logger.warn('[vueLocalCache] read failed', { store, id, err: req.error });
        resolve(undefined);
      };
      tx.oncomplete = () => db.close();
    } catch (err) {
      logger.warn('[vueLocalCache] read threw', { store, id, err });
      db.close();
      resolve(undefined);
    }
  });
}

export async function writeVueCache<T>(
  store: VueCacheStoreName,
  value: T,
  id: string = VUE_CACHE_KEY_DATA,
): Promise<void> {
  const db = await ensureLocalCacheStore(store);
  if (!db) {
    logger.warn(
      '[vueLocalCache] write skipped — could not open or upgrade store',
      { store, id },
    );
    return;
  }
  return new Promise<void>((resolve) => {
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      try { db.close(); } catch { /* already closed */ }
      resolve();
    };
    try {
      const tx = db.transaction(store, 'readwrite');
      const entry: VueCacheEntry<T> = { id, value };
      tx.objectStore(store).put(entry);
      tx.oncomplete = () => {
        logger.info('[vueLocalCache] write committed', { store, id });
        finish();
      };
      tx.onerror = () => {
        logger.warn('[vueLocalCache] write failed', {
          store,
          id,
          err: formatDbError(tx.error),
        });
        finish();
      };
      tx.onabort = () => {
        logger.warn('[vueLocalCache] write aborted', {
          store,
          id,
          err: formatDbError(tx.error),
        });
        finish();
      };
    } catch (err) {
      logger.warn('[vueLocalCache] write threw', { store, id, err });
      finish();
    }
  });
}

export async function clearVueCache(
  store: VueCacheStoreName,
  id: string = VUE_CACHE_KEY_DATA,
): Promise<void> {
  const db = await openLocalCache(store);
  if (!db) return;
  return new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(store, 'readwrite');
      tx.objectStore(store).delete(id);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        db.close();
        resolve();
      };
    } catch {
      db.close();
      resolve();
    }
  });
}
